<script lang="ts" setup>
import type { WebPage } from '~/types/api'
import { InternalRouteName } from '~/constants/internal-route-name'
import type { BreadcrumbsItem } from '~/components/molecules/VBreadcrumbs/VBreadcrumbs.vue'
import type { IpxImage } from '~/types/app'
import type { VPictureHeaderLayout } from '~/components/molecules/VPictureHeader/VPictureHeader.vue'

interface VWebPageHeaderProps {
    webPage: WebPage | null | undefined
    title?: string
    imgSrc?: string
    layout?: VPictureHeaderLayout
    vehicleCategoryType?: string
    hideReservationDialog?: boolean
}

const props = defineProps<VWebPageHeaderProps>()
const pageTitle = computed(() => props.webPage?.title || props.title)

const isWebPageSlug = (slug: string) => props.webPage?.slug === slug

const fallBackImage = computed(() => {
    let imgName = '/car-filigrane.webp'

    if (isWebPageSlug('nos-vehicules-particuliers')) {
        imgName = '/filigrane-voiture.jpg'
    }
    else if (isWebPageSlug('nos-vehicules-utilitaires')) {
        imgName = '/filigrane-utilitaire.jpg'
    }
    else if (isWebPageSlug('minibus-9-places')) {
        imgName = '/filigrane-minibus.jpg'
    }
    else if (isWebPageSlug('vehicule-frigorifique')) {
        imgName = '/filigrane-frigorifique.jpg'
    }

    return {
        relativePath: props.imgSrc || imgName,
        alt: '',
    } as IpxImage
})

const route = useRoute()
const router = useRouter()

const parentRoute = computed(() => {
    return router.options.routes.find((existingRoute) => {
        const matchedRoute = route.path.includes(existingRoute.path)

        if (existingRoute.path === route.path || !matchedRoute) return false

        return route.matched.some(match => match.path.replace('/:slug()', '') === existingRoute.path)
    })
})

const { t } = useI18n()

const breadcrumbItems = computed(() => {
    const home = {
        label: t('pages.web_pages.index.title'),
        route: { name: InternalRouteName.HOME },
    }

    const parentIndex = parentRoute.value && {
        label: t(`pages.${parentRoute.value.name?.toString()}.title`),
        route: parentRoute.value,
    }

    const current = {
        label: pageTitle.value,
        route: route,
    }

    return [home, parentIndex, current].filter(r => !!r) as BreadcrumbsItem[]
})

const headerLayout = computed(() => {
    if (props.layout) return props.layout
    else if (!props.webPage) return 'default'

    return props.webPage?.image?.processable ? 'default' : 'gradient-mask'
})

const rentalFormOpened = defineModel<boolean>({ default: false })
</script>

<template>
    <VHeader
        :title="pageTitle"
        :image="webPage?.image"
        :ipx-image="fallBackImage"
        :breadcrumb-items="breadcrumbItems"
        :layout="headerLayout"
    >
        <template
            v-if="!hideReservationDialog"
            #default
        >
            <VButton
                :class="$style['booking-button']"
                outlined
                :label="$t('rent_a_car.button.search')"
                icon-name="search"
                :theme="headerLayout === 'gradient-mask' ? 'light' : 'dark'"
                @click.prevent="rentalFormOpened = true"
            />
            <ClientOnly>
                <VReservationDialog
                    v-model="rentalFormOpened"
                    :category="vehicleCategoryType"
                />
            </ClientOnly>
        </template>
    </VHeader>
</template>

<style lang="scss" module>
.booking-button {
    position: relative;
    margin-top: rem(24);
    grid-column: 1 / -1;
    justify-self: center;

    @include media('>=md') {
        margin-top: initial;
        grid-column: initial;
        justify-self: flex-end;
    }

}
</style>
